import { useQuery, useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { externalAxios, secureAxios } from "../../../_helpers/auth";
import {
    UNSPLASH_CONFIG,
    PEXELS_CONFIG,
    TENOR_CONFIG,
    PIXABAY_CONFIG,
} from "../config/photo.config";

export const useUnsplashPhotos = (
    query = UNSPLASH_CONFIG.DEFAULT_QUERY,
    page = 1,
    options = {}
) => {
    return useQuery(
        ["unsplashPhotos", query, page],
        async () => {
            const response = await externalAxios.get(
                `${UNSPLASH_CONFIG.BASE_URL}/search/photos`,
                {
                    params: {
                        query,
                        page,
                        per_page: UNSPLASH_CONFIG.PER_PAGE,
                    },
                    headers: {
                        Authorization: `Client-ID ${UNSPLASH_CONFIG.ACCESS_KEY}`,
                    },
                }
            );
            return response.data;
        },
        {
            enabled: !!query && options.enabled !== false,
            staleTime: 1000 * 60 * 5,
        }
    );
};
export const usePexelsPhotos = (
    query = PEXELS_CONFIG.DEFAULT_QUERY,
    page = 1,
    options = {}
) => {
    return useQuery(
        ["pexelsPhotos", query, page],
        async () => {
            const response = await axios.get(
                `${PEXELS_CONFIG.BASE_URL}/search`,
                {
                    params: {
                        query,
                        page,
                        per_page: PEXELS_CONFIG.PER_PAGE,
                    },
                    headers: {
                        Authorization: PEXELS_CONFIG.ACCESS_KEY,
                    },
                }
            );
            return response.data;
        },
        {
            enabled: options.enabled ?? true,
            staleTime: 1000 * 60 * 5,
        }
    );
};

export const useTenorGifs = (
    query = TENOR_CONFIG.DEFAULT_QUERY,
    options = {}
) => {
    return useQuery(
        ["tenorGifs", query],
        async () => {
            const response = await externalAxios.get(
                `${TENOR_CONFIG.BASE_URL}/search`,
                {
                    params: {
                        key: TENOR_CONFIG.ACCESS_KEY,
                        q: query,
                        limit: TENOR_CONFIG.LIMIT,
                    },
                }
            );
            return response.data;
        },
        {
            enabled: !!query && options.enabled !== false,
            staleTime: 1000 * 60 * 5,
        }
    );
};

export const usePixabayPhotos = (
    query = PIXABAY_CONFIG.DEFAULT_QUERY,
    page = 1,
    options = {}
) => {
    return useQuery(
        ["pixabayPhotos", query, page],
        async () => {
            const response = await externalAxios.get(
                `${PIXABAY_CONFIG.BASE_URL}/search`,
                {
                    params: {
                        q: query,
                        page,
                        per_page: PIXABAY_CONFIG.PER_PAGE,
                        image_type: "all",
                    },
                }
            );
            return response.data;
        },
        {
            enabled: !!query && options.enabled !== false,
            staleTime: 1000 * 60 * 5,
        }
    );
};

export const useCollectionFolders = (
    userId,
    { parentId = null, page = 1, perPage = 50 } = {}
) => {
    return useQuery(
        ["collectionFolders", userId, parentId],
        async () => {
            const params = {
                "filter[user_created][_eq]": userId,
                sort: "name",
                page,
                limit: perPage,
            };

            // 🔥 THIS IS THE IMPORTANT PART
            if (parentId === null) {
                params["filter[parent][_null]"] = true;
            } else {
                params["filter[parent][_eq]"] = parentId;
            }

            const res = await secureAxios.get("/folders", { params });
            return res.data?.data ?? res.data;
        },
        { enabled: !!userId }
    );
};

export const useCollectionFiles = (
    userId,
    { folderId = null, page = 1, perPage = 20 } = {}
) => {
    return useQuery(
        ["collectionFiles", userId, folderId, page],
        async () => {
            if (!userId) return null;

            const params = {
                "filter[type][_starts_with]": "image",
                "filter[hidden][_eq]": false,
                "filter[archived][_eq]": false,
                "filter[uploaded_by][_eq]": userId,
                sort: "-uploaded_on",
                page,
                limit: perPage,
            };

            if (folderId === null) {
                params["filter[folder][_null]"] = true;
            } else {
                params["filter[folder][_eq]"] = folderId;
            }

            const res = await secureAxios.get("/files", { params });
            return res.data;
        },
        { enabled: !!userId }
    );
};

// upload hook
export const useUploadFile = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ file, folder = null }) => {
            console.log("📡 useUploadFile mutation START");
            console.log("📡 file:", file);
            console.log("📡 folder param:", folder);
            const formData = new FormData();
            formData.append("file", file);
            if (folder) {
                console.log("📡 formData.append(folder)", folder);
                formData.append("folder", folder);
            } else {
                console.log("📡 NO folder appended (ROOT)");
            }

            const res = await secureAxios.post("/files", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            const data = res?.data?.data;

            if (Array.isArray(data)) return data[0];
            if (data?.id) return data;
            if (res?.data?.id) return res.data;

            throw new Error("Invalid upload response shape");
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    predicate: (q) => q.queryKey[0] === "collectionFiles",
                });
            },
        }
    );
};
export const useCreateFolder = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ name, parent = null }) => {
            const res = await secureAxios.post("/folders", {
                name,
                parent,
            });

            return res.data;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries("collectionFolders");
                queryClient.invalidateQueries("collectionFiles");
            },
        }
    );
};
/*export const useArchiveFile = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async (fileId) => {
            if (!fileId) throw new Error("fileId required");
            await secureAxios.patch(`/files/${fileId}`, {
                archived: true,
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries("collectionFiles");
                queryClient.invalidateQueries("trashFiles");
            },
        }
    );
};*/
export const useUpdateFileArchiveStatus = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ fileId, archived }) => {
            if (!fileId) throw new Error("fileId required");

            await secureAxios.patch(`/files/${fileId}`, {
                archived,
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries("collectionFiles");
                queryClient.invalidateQueries("trashFiles");
            },
        }
    );
};

export const useTrashFiles = (userId, page = 1, perPage = 20) => {
    return useQuery(
        ["trashFiles", userId, page],
        async () => {
            const res = await secureAxios.get("/files", {
                params: {
                    "filter[type][_starts_with]": "image",
                    "filter[archived][_eq]": true,
                    "filter[uploaded_by][_eq]": userId,
                    sort: "-uploaded_on",
                    page,
                    limit: perPage,
                },
            });
            return res.data;
        },
        { enabled: !!userId }
    );
};
//delete permanently
export const usePermanentDeleteFile = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async (fileId) => {
            if (!fileId) throw new Error("fileId required");

            await secureAxios.delete(`/files/${fileId}`);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries("trashFiles");

                queryClient.invalidateQueries("collectionFiles");
            },
        }
    );
};
